import React from "react";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { displayList } from "@auto/widgets/ListComponent";
import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { can, canAny } from "@src/security/permissions";

import {
  USER_ACCOUNT_LIST_FACETS,
  isDefaultUserAccountListFilters
} from "./UserAccountListFacets";

import styles from "./UserAccountList.module.styl";

const DEFAULT_PAGE_SIZE = 50;

let initialRange = null;
let initialRangeUsesLeft = 0;

const translatedOr = (id, fallback) => {
  const value = t(id);
  return value === `[${id}]` ? fallback : value;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric) && String(value).trim() !== "") {
    const date = new Date(numeric);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().replace("T", " ").slice(0, 19);
    }
  }

  return String(value).replace("T", " ").slice(0, 19);
};

const statusCode = (value) =>
  String(value || "").trim().toUpperCase();

const statusLabel = (value) => {
  const raw = String(value || "").trim();
  const labels = {
    ACTIVE: "common.active",
    DISABLED: "common.disabled",
    PENDING: "common.pending",
    APPROVED: "common.approved",
    REJECTED: "common.rejected"
  };

  const key = labels[statusCode(raw)];
  return key ? t(key) : raw;
};

const fullName = (account) =>
  [account?.firstName, account?.lastName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

const centerLabel = (account) => {
  const code = String(account?.votingCenterCode || "").trim();
  const name = String(account?.votingCenterName || "").trim();

  if (code && name) {
    return `${code} · ${name}`;
  }

  return code || name || t("reporting.noCenter");
};

const personImageSrc = (row) => {
  if (!row?.defaultImageId) {
    return "";
  }

  return `${getServiceUri()}person-photo/data/${Number(row.defaultImageId)}`;
};

const OperatorAvatar = ({ row, gui }) => {
  const src = can(gui, "READ_PERSON_PHOTO") ? personImageSrc(row) : "";
  const content = src ? (
    <img
      className={styles.avatarImage}
      src={src}
      alt={row.nationalId || t("person.pictureAlt")}
    />
  ) : (
    <span className={styles.avatarFallback}>
      <AccountCircleOutlinedIcon className={styles.avatarFallbackIcon} />
    </span>
  );

  if (!row?.personGid || !can(gui, "READ_PERSON")) {
    return <span className={styles.avatarShell}>{content}</span>;
  }

  return (
    <button
      type="button"
      className={styles.avatarButton}
      title={t("user.openPersonRecord")}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        gui.goTo(`/person/${row.personGid}`);
      }}
    >
      {content}
    </button>
  );
};

const OperatorNames = ({ row }) => {
  const name = fullName(row);
  const accountCount = Array.isArray(row.accounts) ? row.accounts.length : 0;

  return (
    <div className={styles.operatorCell}>
      <div className={styles.operatorName}>
        {name || row.nationalId || t("voter.operator")}
      </div>

      <div className={styles.operatorMeta}>
        {accountCount} {accountCount === 1 ? "account" : "accounts"}
      </div>
    </div>
  );
};

const SmallAction = ({ title, onClick, children, className = "" }) => (
  <Tooltip title={title}>
    <button
      type="button"
      className={`${styles.accountAction} ${className}`.trim()}
      onClick={onClick}
    >
      {children}
    </button>
  </Tooltip>
);

const AccountCard = ({ account, gui, latest }) => {
  const [currentStatus, setCurrentStatus] = React.useState(account.status);
  const [confirming, setConfirming] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const disabled = statusCode(currentStatus) === "DISABLED";

  const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const openAccount = (event) => {
    stop(event);
    if (account.gid) {
      gui.goTo(`/user-account/${account.gid}`);
    }
  };

  const disable = async (event) => {
    stop(event);

    if (!account.gid || disabled || saving) {
      return;
    }

    setSaving(true);
    try {
      await rest.request(
        getServiceUri() + `user-account-list/${encodeURIComponent(account.gid)}/disable`,
        "POST",
        {}
      );
      setCurrentStatus("DISABLED");
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const cardClassName = [
    styles.accountCard,
    disabled ? styles.accountCardDisabled : "",
    latest ? styles.accountCardLatest : ""
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClassName}>
      <button type="button" className={styles.accountBody} onClick={openAccount}>
        <span className={styles.accountHeader}>
          <span className={styles.accountUsername}>
            {account.username || `#${account.gid}`}
          </span>
          {latest ? (
            <span className={styles.latestBadge}>
              {t("common.latest")}
            </span>
          ) : null}
          {currentStatus ? (
            <span
              className={
                disabled
                  ? `${styles.accountStatus} ${styles.accountStatusDisabled}`
                  : styles.accountStatus
              }
            >
              {statusLabel(currentStatus)}
            </span>
          ) : null}
        </span>
        {account.createdAt ? (
          <span className={styles.accountCreatedAt}>
            {formatDateTime(account.createdAt)}
          </span>
        ) : null}
      </button>

      <div className={styles.accountActions}>
        {account.approvalRequestGid && can(gui, "READ_USER_ACCOUNT_REQUEST") ? (
          <SmallAction
            title={t("user.viewApprovalRequest")}
            onClick={(event) => {
              stop(event);
              gui.goTo(`/user-account-request/${account.approvalRequestGid}`);
            }}
          >
            <AssignmentTurnedInOutlinedIcon fontSize="inherit" />
          </SmallAction>
        ) : null}

        {can(gui, "MANAGE_USER_ACCOUNT") && !disabled && !confirming ? (
          <SmallAction
            title={translatedOr("user.disableAccount", "Disable account")}
            className={styles.disableAction}
            onClick={(event) => {
              stop(event);
              setConfirming(true);
            }}
          >
            <BlockOutlinedIcon fontSize="inherit" />
          </SmallAction>
        ) : null}

        {can(gui, "MANAGE_USER_ACCOUNT") && !disabled && confirming ? (
          <div className={styles.confirmActions}>
            <span className={styles.confirmText}>
              {translatedOr("user.disableAccountConfirm", "Disable?")}
            </span>
            <SmallAction
              title={translatedOr("common.cancel", "Cancel")}
              onClick={(event) => {
                stop(event);
                setConfirming(false);
              }}
            >
              <CloseOutlinedIcon fontSize="inherit" />
            </SmallAction>
            <SmallAction
              title={translatedOr("user.disable", "Disable")}
              className={styles.confirmDisableAction}
              onClick={disable}
            >
              <CheckOutlinedIcon fontSize="inherit" />
            </SmallAction>
          </div>
        ) : null}

        {saving ? (
          <span className={styles.savingText}>
            {translatedOr("common.saving", "Saving")}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const accountCreatedAtValue = (account) => {
  const raw = account?.createdAt;

  if (raw === null || raw === undefined || raw === "") {
    return Number.NEGATIVE_INFINITY;
  }

  const numeric = Number(raw);
  if (Number.isFinite(numeric)) {
    return numeric;
  }

  const parsed = Date.parse(String(raw));
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const latestAccount = (accounts) => {
  const values = Array.isArray(accounts) ? accounts : [];

  if (!values.length) {
    return null;
  }

  return values.reduce((latest, account) => {
    if (!latest) {
      return account;
    }

    return accountCreatedAtValue(account) > accountCreatedAtValue(latest)
      ? account
      : latest;
  }, null);
};

const groupAccountsByVotingCenter = (accounts) => {
  const groups = new Map();

  for (const account of accounts || []) {
    const id = account?.votingCenterId;
    const label = centerLabel(account);
    const key = id !== null && id !== undefined
      ? `id:${id}`
      : `label:${label.toLocaleUpperCase()}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label,
        votingCenterId: id,
        accounts: []
      });
    }

    groups.get(key).accounts.push(account);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      accounts: [...group.accounts].sort(
        (a, b) => accountCreatedAtValue(b) - accountCreatedAtValue(a)
      )
    }))
    .sort((a, b) => {
      const aLatest = accountCreatedAtValue(a.accounts[0]);
      const bLatest = accountCreatedAtValue(b.accounts[0]);

      if (aLatest !== bLatest) {
        return bLatest - aLatest;
      }

      return a.label.localeCompare(b.label);
    });
};

const AccountCards = ({ accounts, gui }) => {
  const values = Array.isArray(accounts) ? accounts : [];
  const newest = latestAccount(values);
  const groups = groupAccountsByVotingCenter(values);

  return (
    <div className={styles.accountGroups}>
      {groups.map((group) => (
        <div key={group.key} className={styles.accountGroup}>
          <div className={styles.accountGroupHeader}>
            <PlaceOutlinedIcon fontSize="inherit" />
            <span className={styles.accountGroupName}>{group.label}</span>
            <span className={styles.accountGroupCount}>{group.accounts.length}</span>
          </div>

          <div className={styles.accountCards}>
            {group.accounts.map((account) => (
              <AccountCard
                key={account.gid || account.username}
                account={account}
                gui={gui}
                latest={account === newest}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ActivityCell = ({ row }) => {
  const first = formatDateTime(row.firstSubmissionAt);
  const last = formatDateTime(row.lastSubmissionAt);

  if (!last) {
    return null;
  }

  return (
    <div className={styles.activityCell}>
      <div className={styles.lastActivity}>{last}</div>
      {first ? (
        <div className={styles.operatorMeta}>
          {t("common.since")} {first}
        </div>
      ) : null}
    </div>
  );
};

const readRangeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit =
    Number.isFinite(limit) && limit > 0
      ? Math.floor(limit)
      : DEFAULT_PAGE_SIZE;

  const safeOffset =
    Number.isFinite(offset) && offset >= 0
      ? Math.floor(offset)
      : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange: params.has("offset") || params.has("limit")
  };
};

const writeRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);
  url.searchParams.set("offset", String(offset));
  url.searchParams.set("limit", String(limit));
  window.history.replaceState({}, "", url.toString());
};

const getEffectiveQuery = (query) => {
  const search = String(query?.search || "").trim();
  const sort = Array.isArray(query?.sort) ? query.sort : [];
  const filters = query?.filters || {};
  const hasActiveFilters = !isDefaultUserAccountListFilters(filters);
  const page = Number.isFinite(query?.page) ? query.page : 0;
  const pageSize = Number.isFinite(query?.pageSize)
    ? query.pageSize
    : DEFAULT_PAGE_SIZE;

  if (
    initialRange &&
    initialRange.hasExplicitRange &&
    initialRangeUsesLeft > 0 &&
    !search &&
    !hasActiveFilters &&
    page === 0 &&
    pageSize === DEFAULT_PAGE_SIZE
  ) {
    initialRangeUsesLeft -= 1;
    return {
      page: initialRange.page,
      pageSize: initialRange.pageSize,
      offset: initialRange.offset,
      limit: initialRange.limit,
      search,
      sort,
      filters
    };
  }

  initialRangeUsesLeft = 0;
  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort,
    filters
  };
};

const selectorsForSortItem = (sortItem) => {
  const column = sortItem?.column;

  if (Array.isArray(column?.sortSelectors)) {
    return column.sortSelectors;
  }
  if (column?.sortSelector) {
    return [column.sortSelector];
  }
  if (sortItem?.field) {
    return [sortItem.field];
  }
  return [];
};

const buildSortPayload = (sort) => {
  const result = [];

  for (const sortItem of sort || []) {
    if (sortItem?.direction !== "asc" && sortItem?.direction !== "desc") {
      continue;
    }

    for (const selector of selectorsForSortItem(sortItem)) {
      if (selector) {
        result.push({ selector, direction: sortItem.direction });
      }
    }
  }

  return result;
};

const buildPayload = (range) => {
  const sort = buildSortPayload(range.sort);

  return {
    search: range.search,
    filters: range.filters,
    limit: range.limit,
    offset: range.offset,
    ...(sort.length ? { sort } : {})
  };
};

const userAccountListProjection = {
  storageKey: "haiti.user-account",
  facets: USER_ACCOUNT_LIST_FACETS,

  columns: [
    {
      title: "",
      field: "defaultImageId",
      sorting: false,
      filtering: false,
      render: (row, gui) => <OperatorAvatar row={row} gui={gui} />
    },
    {
      title: "voter.operator",
      field: "operatorName",
      sortSelectors: ["lastName", "firstName"],
      render: (row) => <OperatorNames row={row} />
    },
    {
      title: "voter.ninu",
      field: "nationalId",
      sortSelector: "nationalId",
      render: (row) => <span className={styles.ninu}>{row.nationalId}</span>
    },
    {
      title: "user.accounts",
      field: "accounts",
      sortSelector: "accountCount",
      filtering: false,
      render: (row, gui) => <AccountCards accounts={row.accounts} gui={gui} />
    },
    {
      title: "reporting.applicants",
      field: "applicationCount",
      sortSelector: "applicationCount",
      filtering: false,
      render: (row) => (
        <span className={styles.metricStrong}>
          {Number(row.applicationCount || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: "reporting.activeDays",
      field: "activeDayCount",
      sortSelector: "activeDayCount",
      filtering: false,
      render: (row) => (
        <span className={styles.metric}>
          {Number(row.activeDayCount || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: "reporting.lastActivity",
      field: "lastSubmissionAt",
      sortSelector: "lastSubmissionAt",
      filtering: false,
      render: (row) => <ActivityCell row={row} />
    },
    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  actions: (row, gui) => (
    canAny(gui, ["READ_STATISTICAL_REPORTS", "READ_OPERATIONAL_REPORTS"]) ? (
      <Tooltip title={t("reporting.operatorReport")}>
      <IconButton
        size="small"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          if (row.nationalId) {
            gui.goTo(`/reporting/operators/${encodeURIComponent(row.nationalId)}`);
          }
        }}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      </Tooltip>
    ) : null
  ),

  fetch: async (query) => {
    const range = getEffectiveQuery(query);
    writeRangeToUrl({ offset: range.offset, limit: range.limit });
    const payload = buildPayload(range);

    const [data, totalCount] = await Promise.all([
      rest.request(getServiceUri() + "user-account-list/search", "POST", payload),
      rest.request(getServiceUri() + "user-account-list/count", "POST", payload)
    ]);

    return {
      data: Array.isArray(data) ? data : [],
      totalCount: Number(totalCount || 0),
      page: range.page
    };
  }
};

export const displayUserAccountList = (gui) => {
  initialRange = readRangeFromUrl();
  initialRangeUsesLeft = initialRange.hasExplicitRange ? 3 : 0;
  return displayList(gui, userAccountListProjection);
};

export default displayUserAccountList;
